import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Medal } from "lucide-react";

interface Props {
  totalConversions: number;
  avgDays: number;
  minDays: number;
  maxDays: number;
  buckets: { within7: number; week1to2: number; week2to4: number; overMonth: number };
  teacherSpeed: { teacherCode: string; count: number; avg: number; min: number; max: number }[];
}

const MEDAL_COLORS = ["text-yellow-500", "text-gray-400", "text-amber-700"];

export function ConversionTimeDashboard({ totalConversions, avgDays, minDays, maxDays, buckets, teacherSpeed }: Props) {
  const barData = [
    { name: "≤ 7 วัน", value: buckets.within7 },
    { name: "1-2 สัปดาห์", value: buckets.week1to2 },
    { name: "2-4 สัปดาห์", value: buckets.week2to4 },
    { name: "> 1 เดือน", value: buckets.overMonth },
  ];

  const summaryCards = [
    { label: "มีข้อมูล conversion", value: totalConversions },
    { label: "เฉลี่ย (วัน)", value: avgDays.toFixed(1) },
    { label: "เร็วสุด (วัน)", value: minDays },
    { label: "ช้าสุด (วัน)", value: maxDays },
  ];

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">ระยะเวลาปิดการขาย</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summaryCards.map((c) => (
            <div key={c.label} className="rounded-lg border bg-muted/50 p-3 text-center">
              <p className="text-xs text-muted-foreground">{c.label}</p>
              <p className="text-xl font-bold mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        <div>
          <p className="text-sm font-medium mb-2">การกระจายระยะเวลา</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" fontSize={12} />
              <YAxis type="category" dataKey="name" fontSize={12} width={100} />
              <Tooltip />
              <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {teacherSpeed.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">อันดับครูปิดการขายเร็วสุด</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">#</th>
                    <th className="text-left p-2">ครู</th>
                    <th className="text-right p-2">จำนวน</th>
                    <th className="text-right p-2">เฉลี่ย</th>
                    <th className="text-right p-2">เร็วสุด</th>
                    <th className="text-right p-2">ช้าสุด</th>
                  </tr>
                </thead>
                <tbody>
                  {teacherSpeed.map((t, i) => (
                    <tr key={t.teacherCode} className="border-b last:border-0">
                      <td className="p-2">
                        {i < 3 ? <Medal className={`h-4 w-4 ${MEDAL_COLORS[i]}`} /> : i + 1}
                      </td>
                      <td className="p-2 font-medium">{t.teacherCode}</td>
                      <td className="p-2 text-right">{t.count}</td>
                      <td className="p-2 text-right">{t.avg.toFixed(1)} วัน</td>
                      <td className="p-2 text-right">{t.min} วัน</td>
                      <td className="p-2 text-right">{t.max} วัน</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
